import React, { useState } from 'react';
import * as XLSX from 'xlsx';
import './OpportunityChecker.css';

const OpportunityChecker = ({ onBack }) => {
  const [file1, setFile1] = useState(null); // Your Opps
  const [file2, setFile2] = useState(null); // All Opps
  const [matches, setMatches] = useState(null);
  const [error, setError] = useState('');
  const [processing, setProcessing] = useState(false);

  // Normalize address: extract street address only, remove suite/unit/apt numbers
  const normalizeAddress = (address) => {
    if (!address) return '';
    return String(address)
      .toLowerCase()
      .split(/\s*[,#]\s*/)[0]
      .replace(/\s+(suite|ste|unit|apt|apartment|bldg|building|floor|fl)\s*\S*$/i, '')
      .replace(/\s+(suite|ste|unit|apt|apartment|bldg|building|floor|fl)\s*$/i, '')
      .replace(/[^a-z0-9\s]/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  };

  // Extract suite/unit number from address
  const extractSuiteNumber = (address) => {
    if (!address) return '';
    const str = String(address);
    // Match suite/unit/apt patterns - avoid matching "FL" state abbreviation
    // Require the keyword to be followed by a space or number, not a comma
    const match = str.match(/(?:suite|ste|unit|apt|apartment|bldg|building|floor|#)\s*([a-z0-9-]+)/i);
    return match ? match[1].toUpperCase() : '';
  };

  const handleFileUpload = (e, setFile) => {
    const file = e.target.files[0];
    if (file) {
      setFile(file);
      setError('');
      setMatches(null);
    }
  };

  const readExcelFile = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet);
          resolve(jsonData);
        } catch (err) {
          reject(err);
        }
      };
      reader.onerror = (err) => reject(err);
      reader.readAsArrayBuffer(file);
    });
  };

  const formatDate = (value) => {
    if (!value) return '';
    // Handle Excel date numbers
    if (typeof value === 'number' && value > 36526 && value < 54789) {
      try {
        const date = XLSX.SSF.parse_date_code(value);
        if (date) {
          return `${String(date.m).padStart(2, '0')}/${String(date.d).padStart(2, '0')}/${date.y}`;
        }
      } catch (e) {
        return String(value);
      }
    }
    // Handle date strings - extract just the date part
    const str = String(value);
    const match = str.match(/^(\d{1,2}\/\d{1,2}\/\d{4})/);
    return match ? match[1] : str;
  };

  const processFiles = async () => {
    if (!file1 || !file2) {
      setError('Please upload both files first.');
      return;
    }

    setProcessing(true);
    setError('');

    try {
      const [yourOpps, allOpps] = await Promise.all([
        readExcelFile(file1),
        readExcelFile(file2)
      ]);

      // Build a map of normalized address -> your opp data
      const yourOppsMap = new Map();
      yourOpps.forEach(row => {
        const locationId = row['Location ID'];
        if (locationId) {
          const normalized = normalizeAddress(locationId);
          if (normalized.length > 0) {
            // Store first occurrence (or could store all)
            if (!yourOppsMap.has(normalized)) {
              yourOppsMap.set(normalized, {
                accountName: row['Account Name'] || '',
                oppName: row['Opportunity Name'] || '',
                oppNumber: row['Opportunity Number'] || '',
                locationId: locationId,
                suiteNumber: extractSuiteNumber(locationId),
                createdDate: formatDate(row['Created Date'])
              });
            }
          }
        }
      });

      if (yourOppsMap.size === 0) {
        throw new Error('No "Location ID" column found or it is empty in "Your Opps" file.');
      }

      // Find matches and build comparison data
      const matchedPairs = [];
      allOpps.forEach(row => {
        const site = row['Site'];
        if (!site) return;

        const normalized = normalizeAddress(site);
        if (normalized.length > 0 && yourOppsMap.has(normalized)) {
          const yourOpp = yourOppsMap.get(normalized);
          const theirSuite = extractSuiteNumber(site);

          // Determine match type
          const isExactMatch = yourOpp.suiteNumber === theirSuite;

          matchedPairs.push({
            // Match info
            matchType: isExactMatch ? 'EXACT' : 'STREET',
            // Your opp data
            yourAccountName: yourOpp.accountName,
            yourOppName: yourOpp.oppName,
            yourOppNumber: yourOpp.oppNumber,
            yourAddress: yourOpp.locationId,
            yourSuite: yourOpp.suiteNumber,
            yourCreatedDate: yourOpp.createdDate,
            // Their opp data
            theirAccountName: row['Account: Account Name'] || row['Account Name'] || '',
            theirOppName: row['Opportunity'] || '',
            theirOppNumber: row['Opportunity Number'] || '',
            theirMRC: row['MRC'] || '',
            theirWBOrderId: row['WB Order ID'] || '',
            theirAddress: site,
            theirSuite: theirSuite,
            theirOAComplete: formatDate(row['OA Complete']),
            theirSalesRep: row['Sales Order Owner'] || ''
          });
        }
      });

      // Sort: EXACT matches first, then by date
      matchedPairs.sort((a, b) => {
        // EXACT matches first
        if (a.matchType === 'EXACT' && b.matchType !== 'EXACT') return -1;
        if (a.matchType !== 'EXACT' && b.matchType === 'EXACT') return 1;
        // Then by your created date (newest first)
        return 0;
      });

      setMatches(matchedPairs);

    } catch (err) {
      console.error('Error processing files:', err);
      setError(err.message || 'Error processing files. Please check the file formats.');
    } finally {
      setProcessing(false);
    }
  };

  const downloadResults = () => {
    if (!matches || matches.length === 0) return;

    // Format for Excel export
    const exportData = matches.map(m => ({
      'Match Type': m.matchType,
      'YOUR Account': m.yourAccountName,
      'YOUR Opp Name': m.yourOppName,
      'YOUR Opp #': m.yourOppNumber,
      'YOUR Address': m.yourAddress,
      'YOUR Suite': m.yourSuite,
      'YOUR Created Date': m.yourCreatedDate,
      'THEIR Account': m.theirAccountName,
      'THEIR Opp Name': m.theirOppName,
      'THEIR Opp #': m.theirOppNumber,
      'THEIR MRC': m.theirMRC,
      'THEIR WB Order ID': m.theirWBOrderId,
      'THEIR Address': m.theirAddress,
      'THEIR Suite': m.theirSuite,
      'THEIR OA Complete': m.theirOAComplete,
      'THEIR Sales Rep': m.theirSalesRep
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Matched Opportunities");

    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    XLSX.writeFile(workbook, `matched_opportunities_${timestamp}.xlsx`);
  };

  return (
    <div className="opportunity-checker">
      <div className="header">
        <button onClick={onBack} className="back-button">← Back to Menu</button>
        <h2>Opportunity Checker</h2>
      </div>

      <div className="upload-section">
        <div className="upload-card">
          <h3>1. Your Opps</h3>
          <div className="file-input-wrapper">
            <input
              type="file"
              accept=".xls,.xlsx"
              onChange={(e) => handleFileUpload(e, setFile1)}
              className="file-input"
            />
          </div>
          {file1 && <div className="file-status success">✓ {file1.name}</div>}
        </div>

        <div className="upload-card">
          <h3>2. All Opps</h3>
          <div className="file-input-wrapper">
            <input
              type="file"
              accept=".xls,.xlsx"
              onChange={(e) => handleFileUpload(e, setFile2)}
              className="file-input"
            />
          </div>
          {file2 && <div className="file-status success">✓ {file2.name}</div>}
        </div>
      </div>

      <div className="process-section">
        {error && <div className="error-message">{error}</div>}
        <button
          onClick={processFiles}
          className="process-button"
          disabled={!file1 || !file2 || processing}
        >
          {processing ? 'Processing...' : 'Find Matches'}
        </button>
      </div>

      {matches && (
        <div className="results-section">
          <div className="results-header">
            <div className="results-title">
              <h3>Found {matches.length} Potential Dupes</h3>
              <div className="match-counts">
                <span className="exact-count">{matches.filter(m => m.matchType === 'EXACT').length} Exact</span>
                <span className="street-count">{matches.filter(m => m.matchType === 'STREET').length} Street Only</span>
              </div>
            </div>
            {matches.length > 0 && (
              <button onClick={downloadResults} className="download-button">
                Download XLS
              </button>
            )}
          </div>

          {matches.length > 0 ? (
            <div className="comparison-list">
              {matches.map((match, index) => (
                <div key={index} className={`comparison-card ${match.matchType.toLowerCase()}-match`}>
                  <div className={`match-type-badge ${match.matchType.toLowerCase()}`}>
                    {match.matchType === 'EXACT' ? '⚠️ EXACT MATCH' : '📍 STREET MATCH'}
                  </div>
                  <div className="comparison-row">
                    <div className="comparison-side yours">
                      <div className="side-label">YOUR OPP</div>
                      <div className="info-grid">
                        <div className="info-item">
                          <span className="label">Account</span>
                          <span className="value">{match.yourAccountName}</span>
                        </div>
                        <div className="info-item">
                          <span className="label">Opp Name</span>
                          <span className="value">{match.yourOppName}</span>
                        </div>
                        <div className="info-item">
                          <span className="label">Opp #</span>
                          <span className="value">{match.yourOppNumber}</span>
                        </div>
                        <div className="info-item full-width">
                          <span className="label">Address</span>
                          <span className="value">{match.yourAddress}</span>
                        </div>
                        {match.yourSuite && (
                          <div className="info-item suite-highlight">
                            <span className="label">Suite/Unit</span>
                            <span className="value">{match.yourSuite}</span>
                          </div>
                        )}
                        <div className="info-item highlight">
                          <span className="label">Created</span>
                          <span className="value">{match.yourCreatedDate}</span>
                        </div>
                      </div>
                    </div>

                    <div className="comparison-divider">
                      <span>VS</span>
                    </div>

                    <div className="comparison-side theirs">
                      <div className="side-label">THEIR OPP</div>
                      <div className="info-grid">
                        <div className="info-item">
                          <span className="label">Account</span>
                          <span className="value">{match.theirAccountName}</span>
                        </div>
                        <div className="info-item">
                          <span className="label">Opp Name</span>
                          <span className="value">{match.theirOppName}</span>
                        </div>
                        <div className="info-item">
                          <span className="label">Opp #</span>
                          <span className="value">{match.theirOppNumber}</span>
                        </div>
                        <div className="info-item">
                          <span className="label">MRC</span>
                          <span className="value">${match.theirMRC}</span>
                        </div>
                        <div className="info-item">
                          <span className="label">WB Order ID</span>
                          <span className="value">{match.theirWBOrderId}</span>
                        </div>
                        <div className="info-item">
                          <span className="label">Sales Rep</span>
                          <span className="value">{match.theirSalesRep}</span>
                        </div>
                        <div className="info-item full-width">
                          <span className="label">Address</span>
                          <span className="value">{match.theirAddress}</span>
                        </div>
                        {match.theirSuite && (
                          <div className="info-item suite-highlight">
                            <span className="label">Suite/Unit</span>
                            <span className="value">{match.theirSuite}</span>
                          </div>
                        )}
                        <div className="info-item highlight">
                          <span className="label">OA Complete</span>
                          <span className="value">{match.theirOAComplete}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="no-matches">
              No dupes found. Street addresses compared (suite/unit numbers ignored).
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default OpportunityChecker;
